/*
 * Avatar provider abstraction.
 *
 * The interview logic (question flow, transcription, scoring, rotation, history)
 * never talks to a specific avatar vendor directly — it only talks to this
 * interface. That means a realtime video provider (e.g. HeyGen LiveAvatar) can
 * be dropped in later by implementing the same interface, WITHOUT rebuilding the
 * interview.
 *
 * Contract:
 *   capabilities()            -> { realtimeVideo: boolean, lipSync: boolean }
 *   async init(panelists)     -> prepare any sessions/assets
 *   async setActiveSpeaker(id)-> mark which panelist is the current speaker
 *   async speak(id, opts)     -> panelist "speaks"; opts may carry { text, playAudio }
 *   async setListening()      -> panel returns to attentive listening state
 *   async stop()              -> tear down sessions/resources
 *   getStream(id)             -> MediaStream for a panelist (realtime video only)
 *
 * The LocalAvatarProvider below is NOT a realtime video avatar. It renders
 * animated photographic portraits. It honestly reports realtimeVideo:false and
 * lipSync:false so the UI never claims to be something it isn't.
 */

export const PANELIST_CONFIG = [
  {
    id: "reyes", // female portraits → female voice (nova)
    name: "Captain Reyes",
    role: "Company Captain",
    style: "Supportive",
    voice: "nova",
    image: "/panel/reyes.jpg",
    variants: ["/panel/reyes.jpg", "/panel/reyes2.jpg", "/panel/reyes3.jpg"],
    color: "#dc2626",
  },
  {
    id: "okafor",
    name: "Battalion Chief Okafor",
    role: "Battalion Chief",
    style: "Tough",
    voice: "onyx",
    image: "/panel/okafor.jpg",
    variants: ["/panel/okafor.jpg", "/panel/okafor2.jpg", "/panel/okafor3.jpg"],
    color: "#2563eb",
  },
  {
    id: "bennett",
    name: "Fire Marshal Bennett",
    role: "Fire Marshal / HR",
    style: "Technical",
    voice: "ash",
    image: "/panel/bennett.jpg",
    variants: ["/panel/bennett.jpg", "/panel/bennett2.jpg", "/panel/bennett3.jpg"],
    color: "#d97706",
  },
];

/*
 * Pick a random face per seat so repeat candidates don't always face the exact
 * same three people. Names, roles and voices stay fixed; only the portrait
 * varies, and the chosen cast is locked for the whole interview.
 */
export function pickPanelCast() {
  return PANELIST_CONFIG.map((p) => {
    const pool = p.variants && p.variants.length ? p.variants : [p.image];
    return { ...p, image: pool[Math.floor(Math.random() * pool.length)] };
  });
}

export function panelistByName(name = "") {
  const key = name.split(" ").pop().toLowerCase();
  return PANELIST_CONFIG.find((p) => p.id === key) || null;
}

export class AvatarProvider {
  constructor() {
    this.panelists = [];
    this.activeSpeakerId = null;
    this.state = "idle"; // idle | speaking | listening
  }
  capabilities() {
    return { realtimeVideo: false, lipSync: false };
  }
  async init(panelists) {
    this.panelists = panelists;
  }
  async setActiveSpeaker(id) {
    this.activeSpeakerId = id;
  }
  async speak(id /*, opts */) {
    this.activeSpeakerId = id;
    this.state = "speaking";
  }
  async setListening() {
    this.state = "listening";
  }
  async stop() {
    this.state = "idle";
    this.activeSpeakerId = null;
  }
  getStream(/* id */) {
    return null;
  }
}

/*
 * LocalAvatarProvider — no external/paid API.
 * Realistic photographic portraits animated with CSS (breathing, subtle head
 * sway, speaking motion). It does not stream video and does not lip-sync; audio
 * playback is owned by the interview page (existing OpenAI TTS).
 */
export class LocalAvatarProvider extends AvatarProvider {
  capabilities() {
    return { realtimeVideo: false, lipSync: false };
  }
}

/*
 * Realtime provider seam (NOT implemented — intentionally).
 * To enable realtime video avatars later (HeyGen LiveAvatar etc.), implement a
 * subclass here that:
 *   - init(): mints a session token via the BACKEND (never expose the API key),
 *             starts a LITE-mode session per active panelist, joins the LiveKit room
 *   - getStream(id): returns the panelist's live MediaStream (attached to <video>)
 *   - speak(id, { getAudio }): streams PCM audio to the provider for lip-synced video
 *   - setListening()/stop(): pose + teardown
 * capabilities() must then return { realtimeVideo:true, lipSync:true } so
 * OralBoardPanel switches from portraits to live <video> automatically.
 */
export class RealtimeAvatarProviderStub extends AvatarProvider {
  capabilities() {
    return { realtimeVideo: true, lipSync: true };
  }
  async init() {
    throw new Error(
      "Realtime avatar provider is not configured. Provide backend credentials and implement this provider to enable realtime video."
    );
  }
}

export function createAvatarProvider(kind = "local") {
  switch (kind) {
    case "realtime":
      return new RealtimeAvatarProviderStub();
    case "local":
    default:
      return new LocalAvatarProvider();
  }
}
